// Selector de premios individuales del Mundial (Balón de Oro, Bota de Oro, etc.)
import { useState } from 'react'
import { motion } from 'framer-motion'
import { Award, Target, Search } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import client from '../../api/client'
import Card from '../ui/Card'
import Input from '../ui/Input'
import Badge from '../ui/Badge'
import Skeleton from '../ui/Skeleton'
import Flag from '../ui/Flag'

// Categorías de premios individuales que corresponden a la base de datos
const awardCategories = [
  {
    key: 'mejor_jugador',
    label: 'Balón de Oro (MVP)',
    description: 'Mejor jugador del torneo',
    icon: Award,
    color: 'text-warning',
  },
  {
    key: 'maximo_goleador',
    label: 'Bota de Oro (Goleador)',
    description: 'Máximo goleador del torneo',
    icon: Target,
    color: 'text-warning',
  },
]

export default function AwardsPicker({ predictions, onUpdate }) {
  const [activeCategory, setActiveCategory] = useState('mejor_jugador')
  const [searchQuery, setSearchQuery] = useState('')

  // Obtener todos los jugadores de la API
  const { data: playersData, isLoading } = useQuery({
    queryKey: ['players'],
    queryFn: async () => {
      const response = await client.get('/players')
      return response.data
    },
    staleTime: 10 * 60 * 1000, // 10 minutos
  })

  const players = playersData?.datos || []

  // Filtrar jugadores según búsqueda
  const filteredPlayers = players.filter(player =>
    player.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (player.equipo_nombre && player.equipo_nombre.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  // Seleccionar un jugador para un premio
  const handleSelect = (categoryKey, player) => {
    onUpdate({
      ...predictions,
      [categoryKey]: player,
    })
  }

  // Contar premios completados
  const completedAwards = awardCategories.filter(c => predictions?.[c.key]).length

  return (
    <div>
      {/* Encabezado */}
      <div className="mb-6">
        <h3 className="text-xl font-display font-bold text-text-primary mb-2">
          Premios Individuales
        </h3>
        <p className="text-sm text-text-secondary mb-3">
          Predice quién ganará cada premio individual del Mundial 2026.
        </p>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-surface-card rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-warning rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${(completedAwards / awardCategories.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <span className="text-sm font-mono text-warning font-semibold">
            {completedAwards}/{awardCategories.length}
          </span>
        </div>
      </div>

      {/* Tarjetas de resumen de premios */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {awardCategories.map((award) => {
          const isActive = activeCategory === award.key
          const selectedPlayer = predictions?.[award.key]
          const AwardIcon = award.icon

          return (
            <motion.button
              key={award.key}
              whileHover={{ y: -2 }}
              onClick={() => {
                setActiveCategory(award.key)
                setSearchQuery('')
              }}
              className={`
                p-4 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer
                ${isActive
                  ? 'border-cyan bg-cyan/5 glow-cyan'
                  : selectedPlayer
                    ? 'border-success/30 bg-success/5'
                    : 'border-border-default bg-surface-card hover:border-border-strong'
                }
              `}
            >
              <AwardIcon className={`w-6 h-6 mb-2 ${award.color}`} />
              <p className="text-xs font-display font-semibold text-text-primary mb-0.5">
                {award.label}
              </p>
              {selectedPlayer ? (
                <p className="text-xs text-success font-medium truncate flex items-center">
                  <Flag code={selectedPlayer.bandera_equipo || selectedPlayer.equipo_bandera} className="w-4 h-3 mr-1 object-cover rounded shadow-sm shrink-0" />
                  <span>{selectedPlayer.nombre}</span>
                </p>
              ) : (
                <p className="text-xs text-text-muted">Sin seleccionar</p>
              )}
            </motion.button>
          )
        })}
      </div>

      {/* Panel de selección de jugador */}
      <Card variant="default" padding="lg">
        <div className="mb-4">
          <h4 className="font-display font-semibold text-text-primary mb-1">
            {awardCategories.find(c => c.key === activeCategory)?.label}
          </h4>
          <p className="text-xs text-text-muted">
            {awardCategories.find(c => c.key === activeCategory)?.description}
          </p>
        </div>

        {/* Barra de búsqueda */}
        <Input
          icon={Search}
          placeholder="Buscar jugador por nombre o selección..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          containerClassName="mb-4"
        />

        {/* Lista de jugadores */}
        {isLoading ? (
          <div className="space-y-2 py-4">
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
            {filteredPlayers.map((player) => {
              const isSelected = predictions?.[activeCategory]?.id === player.id

              return (
                <motion.button
                  key={player.id}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => handleSelect(activeCategory, player)}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg text-left cursor-pointer
                    transition-all duration-200
                    ${isSelected
                      ? 'bg-cyan/10 border border-cyan text-cyan'
                      : 'bg-surface-card hover:bg-surface-card-hover border border-transparent'
                    }
                  `}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isSelected ? 'text-cyan' : 'text-text-primary'}`}>
                      {player.nombre}
                    </p>
                    <p className="text-xs text-text-muted truncate flex items-center gap-1">
                      <Flag code={player.equipo_bandera} className="w-4 h-3 object-cover rounded shadow-sm shrink-0" />
                      <span>{player.equipo_nombre || ''} · {player.posicion}</span>
                    </p>
                  </div>
                  {isSelected && (
                    <Badge variant="cyan" size="sm">Seleccionado</Badge>
                  )}
                </motion.button>
              )
            })}

            {filteredPlayers.length === 0 && (
              <div className="col-span-2 text-center py-8">
                <p className="text-sm text-text-muted">No se encontraron jugadores</p>
                <p className="text-xs text-text-muted mt-1">Intenta con otro nombre o selección</p>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}
